import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Button
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from "@mui/icons-material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { GetUserLogReportNew } from "../../services/api";
import useLogReportStyles from "./styles";

const SEVERITY_COLORS = {
  INFO: 'info',
  WARN: 'warning',
  ERROR: 'error',
  CRITICAL: 'error',
  DEBUG: 'default'
};

const LogReport = ({ token }) => {
  const classes = useLogReportStyles();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters State
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    operation: 'all',
    severity: 'all',
    fromDate: dayjs().subtract(7, 'days'),
    toDate: dayjs(),
  });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalLogs, setTotalLogs] = useState(0);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        startDate: filters.fromDate.toISOString(),
        endDate: filters.toDate.toISOString(),
      };

      if (filters.search) params.search = filters.search;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.operation !== 'all') params.operation = filters.operation;
      if (filters.severity !== 'all') params.severity = filters.severity;

      const response = await GetUserLogReportNew(token, params);
      if (response?.data?.status) {
        setLogs(response.data.data);
        setTotalLogs(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, filters.fromDate, filters.toDate, filters.category, filters.operation, filters.severity]);

  const handleSearchChange = (event) => {
    setFilters(prev => ({ ...prev, search: event.target.value }));
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#ffffffff', minHeight: '100%' }}>
      {/* Header Area */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary.main">
            System Logs & Audit Trail
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor system activities and user operations across microservices
          </Typography>
        </Box>
        <IconButton onClick={fetchLogs} color="primary" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Email or Message"
              value={filters.search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                label="Category"
                onChange={handleFilterChange('category')}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="USER_MANAGEMENT">User Management</MenuItem>
                <MenuItem value="AUTHENTICATION">Auth</MenuItem>
                <MenuItem value="EMAIL_OPERATIONS">Email</MenuItem>
                <MenuItem value="SYSTEM_OPERATIONS">System</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Operation</InputLabel>
              <Select
                value={filters.operation}
                label="Operation"
                onChange={handleFilterChange('operation')}
              >
                <MenuItem value="all">All Ops</MenuItem>
                <MenuItem value="CREATE">Create</MenuItem>
                <MenuItem value="UPDATE">Update</MenuItem>
                <MenuItem value="DELETE">Delete</MenuItem>
                <MenuItem value="LOGIN">Login</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                value={filters.fromDate}
                onChange={(val) => setFilters(prev => ({ ...prev, fromDate: val }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={filters.toDate}
                onChange={(val) => setFilters(prev => ({ ...prev, toDate: val }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchLogs}
              style={{ width: '100%', height: '40px' }}
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Actor</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Message</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  <Tooltip title={log.id}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      ...{log.id.slice(-6)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {dayjs(log.timestamp).format('DD MMM YYYY')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(log.timestamp).format('HH:mm:ss')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {log.actor?.name || 'System'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {log.actor?.email || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={log.operation}
                    size="small"
                    variant="outlined"
                    color={log.operation === 'DELETE' ? 'error' : 'primary'}
                    sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                    {log.category?.toLowerCase().replace('_', ' ')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                  <Typography variant="body2" noWrap sx={{ cursor: 'pointer' }}>
                    {log.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {log.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={log.operationStatus}
                    size="small"
                    color={log.operationStatus === 'SUCCESS' ? 'success' : 'error'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No log records found for the selected period.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalLogs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default LogReport;
